'use client'

// Keeps ONE fresh file pass per tab for a logged-in member and hands it to every file link
// on the page (why a pass exists at all: see filePass.ts). The pass must already be in the
// link when it is tapped: fetching it on tap would turn the new-tab open into a blocked
// popup. So it is fetched when a file link mounts and renewed while the tab is visible,
// well before it runs out.
import { useEffect, useSyncExternalStore } from 'react'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { FILE_PASS_TTL_SECONDS } from './filePass'
import { needsFilePass, withFilePass } from './fileLinks'

let pass: string | null = null
let expiresAt = 0
let inflight: Promise<void> | null = null
let consumers = 0
let timer: number | undefined
const listeners = new Set<() => void>()

// Renew once less than half the lifetime is left; look every 30 s.
const MIN_LIFE_MS = (FILE_PASS_TTL_SECONDS * 1000) / 2
const CHECK_EVERY_MS = 30_000

const publish = (nextPass: string | null, nextExpiresAt: number) => {
  pass = nextPass
  expiresAt = nextExpiresAt
  listeners.forEach((l) => l())
}

const renew = () => {
  if (inflight) return
  inflight = fetch('/api/file-pass', { cache: 'no-store' })
    .then(async (res) => {
      if (!res.ok) return publish(null, 0)
      const body = (await res.json()) as { pass?: string; expiresAt?: number }
      publish(body.pass || null, body.expiresAt || 0)
    })
    // offline or blocked: links simply go out without a pass (the session cookie still works)
    .catch(() => {})
    .finally(() => {
      inflight = null
    })
}

const renewIfStale = () => {
  if (document.visibilityState === 'visible' && expiresAt - Date.now() < MIN_LIFE_MS) renew()
}

const start = () => {
  if (consumers++ > 0) return
  renewIfStale()
  timer = window.setInterval(renewIfStale, CHECK_EVERY_MS)
  document.addEventListener('visibilitychange', renewIfStale)
  window.addEventListener('focus', renewIfStale)
}

const stop = () => {
  if (--consumers > 0) return
  window.clearInterval(timer)
  document.removeEventListener('visibilitychange', renewIfStale)
  window.removeEventListener('focus', renewIfStale)
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The link a logged-in member should follow for one of our files: the file URL plus their
 * file pass when the file is served through the middleware gate. Logged-out visitors and
 * files that need no pass (Blob store) get the URL back unchanged.
 */
export function useMemberFileHref(url: string | null | undefined): string {
  const { user } = useSupabaseAuth()
  const enabled = Boolean(user) && needsFilePass(url)
  const current = useSyncExternalStore(
    subscribe,
    () => pass,
    () => null,
  )

  useEffect(() => {
    if (!enabled) return
    start()
    return stop
  }, [enabled])

  return withFilePass(url || '#', enabled ? current : null)
}
